import React, { forwardRef, useCallback, useMemo, useState } from "react";

import { Button, Card, Drawer, Fab, Grid, Typography, LinearProgress, Box } from "@mui/material";
import {
  Camera as CameraIcon,
  CreateNewFolder as CreateNewFolderIcon,
  Image as ImageIcon,
  Upload as UploadIcon,
  Note as NoteIcon,
} from "@mui/icons-material";
import { createFolder } from "./app/transfer";
import { useUploadEnqueue } from "./app/transferQueue";

function IconCaptionButton({
  icon,
  caption,
  onClick,
}: {
  icon: React.ReactNode;
  caption: string;
  onClick?: () => void;
}) {
  return (
    <Button
      color="inherit"
      sx={{ width: "100%", display: "flex", flexDirection: "column" }}
      onClick={onClick}
    >
      {icon}
      <Typography
        variant="caption"
        sx={{ textTransform: "none", textWrap: "nowrap" }}
      >
        {caption}
      </Typography>
    </Button>
  );
}

export const UploadFab = forwardRef<HTMLButtonElement, { onClick: () => void }>(
  function ({ onClick }, ref) {
    return (
      <Fab
        ref={ref}
        aria-label="上传"
        variant="circular"
        color="primary"
        size="large"
        sx={{ position: "fixed", right: 16, bottom: 16, color: "white" }}
        onClick={onClick}
      >
        <UploadIcon fontSize="large" />
      </Fab>
    );
  }
);

/**
 * 上传抽屉组件
 * @param open - 是否打开
 * @param setOpen - 设置打开状态
 * @param cwd - 当前工作目录
 * @param onUpload - 上传完成回调
 */
function UploadDrawer({
  open,
  setOpen,
  cwd,
  onUpload,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  cwd: string;
  onUpload: () => void;
}) {
  const uploadEnqueue = useUploadEnqueue();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleUpload = useCallback(
    (action: string) => () => {
      const input = document.createElement("input");
      input.type = "file";
      switch (action) {
        case "photo": // 拍照
          input.accept = "image/*";
          input.capture = "environment";
          break;
        case "image": // 图片/视频
          input.accept = "image/*,video/*";
          break;
        case "file": // 所有文件
          input.accept = "*/*";
          break;
      }
      input.multiple = true;
      input.onchange = async () => {
        if (!input.files) return;
        const files = Array.from(input.files);
        setUploading(true);
        setUploadProgress(0);
        
        try {
          const totalSize = files.reduce((acc, file) => acc + file.size, 0);
          let currentProgress = 0;
          
          const updateProgress = (additionalProgress: number) => {
            currentProgress += additionalProgress;
            const progress = Math.min((currentProgress / totalSize) * 100, 100);
            setUploadProgress(progress);
            return progress;
          };

          for (let i = 0; i < files.length; i++) {
            const file = files[i];
            await new Promise<void>((resolve) => {
              const upload = { file, basedir: cwd };
              uploadEnqueue(upload);
              
              let fileProgress = 0;
              const interval = setInterval(() => {
                const chunk = file.size / 10;
                if (fileProgress < file.size) {
                  fileProgress += chunk;
                  const progress = updateProgress(chunk);
                  if (progress >= 100) {
                    clearInterval(interval);
                    resolve();
                  }
                } else {
                  clearInterval(interval);
                  resolve();
                }
              }, 200);
            });
          }
        } finally {
          setUploading(false);
          setOpen(false);
          onUpload();
        }
      };
      input.click();
    },
    [cwd, onUpload, setOpen, uploadEnqueue]
  );

  const takePhoto = useMemo(() => handleUpload("photo"), [handleUpload]);
  const uploadImage = useMemo(() => handleUpload("image"), [handleUpload]);
  const uploadFile = useMemo(() => handleUpload("file"), [handleUpload]);

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={() => setOpen(false)}
      PaperProps={{ sx: { borderRadius: "16px 16px 0 0" } }}
    >
      {uploading && (
        <Card sx={{
          position: 'fixed',
          top: 16,
          right: 16,
          zIndex: 9999,
          padding: 2,
          width: 300,
          boxShadow: 3,
        }}>
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              正在上传文件...
            </Typography>
            <LinearProgress variant="determinate" value={uploadProgress} />
            <Typography variant="caption" sx={{ display: 'block', textAlign: 'right', mt: 0.5 }}>
              {Math.round(uploadProgress)}%
            </Typography>
          </Box>
        </Card>
      )}
      <Card sx={{ padding: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={2.4}>
            <IconCaptionButton
              icon={<CameraIcon fontSize="large" />}
              caption="拍照"
              onClick={takePhoto}
            />
          </Grid>
          <Grid item xs={2.4}>
            <IconCaptionButton
              icon={<ImageIcon fontSize="large" />}
              caption="图片/视频"
              onClick={uploadImage}
            />
          </Grid>
          <Grid item xs={2.4}>
            <IconCaptionButton
              icon={<UploadIcon fontSize="large" />}
              caption="上传文件"
              onClick={uploadFile}
            />
          </Grid>
          <Grid item xs={2.4}>
            <IconCaptionButton
              icon={<NoteIcon fontSize="large" />}
              caption="新建文本"
              onClick={async () => {
                setOpen(false);
                const filename = window.prompt("请输入文件名:", "新建文本.txt");
                if (filename) {
                  const textarea = document.createElement('textarea');
                  textarea.style.position = 'fixed';
                  textarea.style.top = '50%';
                  textarea.style.left = '50%';
                  textarea.style.transform = 'translate(-50%, -50%)';
                  textarea.style.width = '80%';
                  textarea.style.height = '80%';
                  textarea.style.zIndex = '9999';
                  textarea.style.padding = '10px';
                  textarea.style.border = '1px solid #ccc';
                  textarea.style.backgroundColor = 'white';
                  
                  const saveButton = document.createElement('button');
                  saveButton.textContent = '保存';
                  saveButton.style.position = 'fixed';
                  saveButton.style.right = '11%';
                  saveButton.style.top = '11%';
                  saveButton.style.zIndex = '10000';
                  saveButton.style.padding = '5px 15px';
                  saveButton.style.backgroundColor = '#f38020';
                  saveButton.style.color = 'white';
                  saveButton.style.border = 'none';
                  saveButton.style.borderRadius = '4px';
                  saveButton.style.cursor = 'pointer';

                  document.body.appendChild(textarea);
                  document.body.appendChild(saveButton);

                  saveButton.onclick = () => {
                    const file = new File([textarea.value], filename, { type: "text/plain" });
                    uploadEnqueue({ file, basedir: cwd });
                    onUpload();
                    textarea.remove();
                    saveButton.remove();
                  };
                }
              }}
            />
          </Grid>
          <Grid item xs={2.4}>
            <IconCaptionButton
              icon={<CreateNewFolderIcon fontSize="large" />}
              caption="新建文件夹"
              onClick={async () => {
                setOpen(false);
                await createFolder(cwd);
                onUpload();
              }}
            />
          </Grid>
        </Grid>
      </Card>
    </Drawer>
  );
}

export default UploadDrawer;
